import { brandfolderColorPalette } from '@brandfolder/react';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import styled, { createGlobalStyle } from 'styled-components';

import Tooltip from '@components/common/tooltip/main';

import { Person } from '../customPeopleTagTypes';
const SuggestedList = styled.ul`
  background-color: ${brandfolderColorPalette.neutrals[0]};
  box-shadow: 1px 2px 6px 0 rgba(0, 0, 0, 0.09);
  border-radius: 4px;
  list-style: none;
  padding: 4px;
  position: absolute;
  margin-top: 2px;
  z-index: 2;
  max-height: 280px;
  overflow-y: auto;
  width: 120%;
`;

const AutocompleteOption = styled.li`
  font-size: 14px;
`;

const AutocompleteOptionButton = styled.button`
  align-items: center;
  background-color: transparent;
  border: none;
  display: flex;
  cursor: pointer;
  padding: 8px;
  text-align: left;
  width: 100%;
  gap: 4px;

  &:hover {
    background-color: ${brandfolderColorPalette.neutrals[100]};
    border-radius: 4px;
  }
`;

const AutocompleteOptionImage = styled.img`
  border-radius: 2px;
  height: 40px;
  width: 40px;
  object-fit: cover;
  flex-shrink: 0;
`;

const AutocompleteOptionName = styled.span`
  font-size: 14px;
  font-weight: 450;
  color: ${brandfolderColorPalette.neutrals[700]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`;

const AutocompleteOptionTagCount = styled.span`
  color: ${brandfolderColorPalette.neutrals[400]};
  font-size: 14px;
  font-weight: 400;
  margin-left: 4px;
`;

const TooltipLayoutFix = createGlobalStyle`
  .person-details-suggested-names-list .tooltip-component,
  .suggested-names-list .tooltip-component {
    display: contents;
  }
  .person-details-suggested-names-list .tooltip-component > span,
  .suggested-names-list .tooltip-component > span {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

interface PersonNameWithTooltipProps {
  personId: string;
  personName: string;
}

const PersonNameWithTooltip: FunctionComponent<PersonNameWithTooltipProps> = ({ personId, personName }) => {
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const nameTextRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const checkTruncation = (): void => {
      if (nameTextRef.current && personName) {
        const element = nameTextRef.current;
        // Check if text is actually truncated by comparing scroll width to client width
        setIsTextTruncated(element.scrollWidth > element.clientWidth);
      } else {
        setIsTextTruncated(false);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(checkTruncation);
    }, 0);

    // Recheck on window resize to handle responsive layout changes
    window.addEventListener('resize', checkTruncation);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [personName]);

  return (
    <>
      {isTextTruncated ? (
        <Tooltip
          placement="top"
          tooltipContent={personName}
          tooltipId={`suggested-name-tooltip-${personId}`}
          type="dark"
        >
          <AutocompleteOptionName ref={nameTextRef}>{personName}</AutocompleteOptionName>
        </Tooltip>
      ) : (
        <AutocompleteOptionName ref={nameTextRef}>{personName}</AutocompleteOptionName>
      )}
    </>
  );
};

interface SuggestedNamesListProps {
  handleOnSelectTargetPerson: (person: Person) => void;
  handleCreateNewPerson?: (typedName: string) => void;
  suggestions: Person[];
  typedName?: string;
  showCreateOption?: boolean;
  listClasses?: string;
}

const CreateOption = styled.li`
  .create-option-button {
    align-items: center;
    background-color: transparent;
    border: none;
    display: flex;
    cursor: pointer;
    gap: 8px;
    padding: 8px 12px;
    text-align: left;
    width: 100%;
    font-size: 14px;
    font-weight: 500;
    color: ${brandfolderColorPalette.neutrals[700]};

    &:hover {
      background-color: ${brandfolderColorPalette.neutrals[100]};
      border-radius: 4px;
    }
  }
`;

export const SuggestedNamesList: FunctionComponent<SuggestedNamesListProps> = ({
  suggestions,
  handleOnSelectTargetPerson,
  handleCreateNewPerson,
  typedName,
  showCreateOption = false,
  listClasses = null
}) => {
  const hasSuggestions = suggestions?.length > 0;
  const canCreateNewPerson = showCreateOption && typedName?.trim() && handleCreateNewPerson;
  const shouldShowList = hasSuggestions || canCreateNewPerson;

  if (!shouldShowList) {
    return null;
  }

  return (
    <>
      <TooltipLayoutFix />
      <SuggestedList key={`suggested-names-list-${typedName}`} className={listClasses}>
        {canCreateNewPerson && (
          <CreateOption>
            <button
              className="create-option-button"
              onClick={(): void => handleCreateNewPerson(typedName.trim())}
              type="button"
            >
              <span>Create "{typedName.trim()}"</span>
            </button>
          </CreateOption>
        )}
        {suggestions.map((person) => (
          <AutocompleteOption key={person.face_uri}>
            <AutocompleteOptionButton
              id={person.person_id}
              onClick={(): void => handleOnSelectTargetPerson(person)}
              type="button"
            >
              <AutocompleteOptionImage alt="person thumbnail" src={person.face_uri} />
              <PersonNameWithTooltip personId={person.person_id} personName={person.tag_name} />
              {person.face_count ? <AutocompleteOptionTagCount>{person.face_count}</AutocompleteOptionTagCount> : null}
            </AutocompleteOptionButton>
          </AutocompleteOption>
        ))}
      </SuggestedList>
    </>
  );
};
